<template>
	<v-container>
		<v-form
			ref="checkoutInfoForm"
		>
			<v-row>
				<v-col>
					<h3>
						<translate>
							Your details
						</translate>
					</h3>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-radio-group
						v-model:model-value="information.gender"
						inline
						:rules="[v => !!v || $gettext('This field is required')]"
					>
						<v-radio
							value="M"
							:label="$gettext('Male')"
							class="mb-0"
							color="primary"
						/>
						<v-radio
							value="F"
							:label="$gettext('Female')"
							class="mb-0"
							color="primary"
						/>
					</v-radio-group>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-text-field
						v-model:model-value="information.firstName"
						:label="$gettext('First name')"
						:rules="[v => !!v || $gettext('First name is required')]"
						placeholder="Jeroen"
						persistent-placeholder
						validate-on="blur"
						variant="outlined"
						hide-details="auto"
						required
					>
						<template
							v-if="information.firstName && information.firstName.length > 0"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col
					cols="12"
					sm="6"
					style="padding: 12px !important;"
				>
					<v-text-field
						v-model:model-value="information.infix"
						:label="$gettext('Infix')"
						placeholder="van der"
						persistent-placeholder
						validate-on="blur"
						variant="outlined"
						hide-details="auto"
					>
						<template
							v-if="information.infix && information.infix.length > 0"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
				<v-col
					cols="12"
					sm="6"
					style="padding: 12px !important;"
				>
					<v-text-field
						v-model:model-value="information.lastName"
						:label="$gettext('Last name')"
						placeholder="Jansen"
						persistent-placeholder
						validate-on="blur"
						variant="outlined"
						required
						hide-details="auto"
						:rules="[v => !!v || $gettext('Last name is required')]"
					>
						<template
							v-if="information.lastName && information.lastName.length > 0"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row style="margin-bottom: 12px;">
				<v-col>
					<date-text-field
						v-model:model-value="information.birthDate"
						prepend-inner-icon="$calendar"
						label="Birth date"
						append-on-valid="$check"
						variant="outlined"
						persistent-placeholder
						validate-on="blur"
						clearable
						hide-details="auto"
						required
						:custom-validator="validateDate"
					/>
				</v-col>
			</v-row>
			<address-fields
				ref="addressFields"
				v-model:model-value="information.address"
				enable-placeholders
				enable-autofill
				validate-on="blur"
				append-on-valid="$check"
				country-name="Netherlands"
				variant="outlined"
				hide-details="auto"
				persistent-placeholder
			>
				<template
					#append-postalcode="props"
				>
					<div /> <!--force slot override-->
					<div v-if="props.address.postalcode && props.address.postalcode.length > 0 && /^\d{4} ?[a-zA-Z]{2}$/.test(props.address.postalcode)">
						<v-icon
							color="primary"
							icon="$check"
						/>
					</div>
				</template>
			</address-fields>
			<v-row style="margin-top: 12px;">
				<v-col>
					<v-text-field
						v-model:model-value="information.phoneNumber"
						:label="$gettext('Telephone number')"
						placeholder="+31 600000000"
						persistent-placeholder
						validate-on="blur"
						variant="outlined"
						required
						hide-details="auto"
						:rules="[
							v => !!v || $gettext('Please fill in your phone number'),
							v => /^\+(?:[0-9] ?){6,14}[0-9]$/.test(v) || $gettext('Telephone number must be in the international format and should start with a plus sign (+)')
						]"
					>
						<template
							v-if="information.phoneNumber && /^\+(?:[0-9] ?){6,14}[0-9]$/.test(information.phoneNumber)"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row>
				<v-col>
					<v-text-field
						v-model:model-value="information.emailAddress"
						:label="$gettext('Email address')"
						placeholder="j.jansen@mijnweb.nl"
						persistent-placeholder
						validate-on="blur"
						variant="outlined"
						hide-details="auto"
						required
						:rules="[v => !!v || $gettext('Email address is required'),
							v => emailRegex.test(v) || $gettext('Please enter a valid email address')]"
						@focus="showEmailText = false"
						@blur="showEmailText = true"
					>
						<template
							v-if="information.emailAddress && emailRegex.test(information.emailAddress)"
							#append-inner
						>
							<v-icon
								color="primary"
								icon="$check"
							/>
						</template>
					</v-text-field>
				</v-col>
			</v-row>
			<v-row v-if="information.emailAddress && emailRegex.test(information.emailAddress) && showEmailText">
				<v-col>
					<span style="color: #68bd45">
						<translate>We will send you a confirmation email on</translate> {{ information.emailAddress }}
					</span>
				</v-col>
			</v-row>
		</v-form>
	</v-container>
</template>

<script>
import AddressFields from '../organisms/AddressFields.vue';
import DateTextField from '../atoms/DateTextField.vue';

export default {
	name: 'CheckoutInformation',
	components: {
		DateTextField,
		AddressFields
	},

	props: {
		modelValue: {
			type: Object,
			default: null
		}
	},

	emits: ['update:modelValue',],

	data () {
		return {
			information: {
				...this.modelValue
			},
			showEmailText: false,
			emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
		};
	},

	watch: {
		modelValue: {
			/**
			 * Watch the value passed to the component and set if changed.
			 */
			handler () {
				this.information = this.modelValue;
			},
			deep: true
		},

		information: {
			/**
			 * Watch if any changes are made in the form and emit to parent
			 */
			handler () {
				this.$emit('update:modelValue', this.information);
			},
			deep: true
		}
	},

	methods: {
		/**
		 * Validate the form
		 *
		 * @returns {*}
		 */
		async validateInformation () {
			const {
				valid
			} = await this.$refs.checkoutInfoForm.validate();

			return valid && this.$refs.addressFields.validateAddress();
		},

		/**
		 * Ensure the entered date is at least 18 years ago
		 *
		 * @param {string} value
		 * @returns {boolean|string}
		 */
		validateDate (value) {
			const validDate = new Date();
			const date = new Date(value);

			validDate.setFullYear(validDate.getFullYear() - 18);
			return ((date <= validDate) || this.$gettext('Insurance can only be applied for by customers who are 18 years of age or older'));
		}
	}
};
</script>
